import { router, useFocusEffect } from "expo-router";
import { type User } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { BadgeCheck } from "lucide-react-native";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  Alert,
  AppState,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

import { SokoGridLogo } from "@/components/brand/SokoGridLogo";
import { LegalAcceptance } from "@/components/legal/LegalAcceptance";
import { useOwnerAuth } from "@/components/owner/OwnerAuthProvider";
import { getActiveEntitlement } from "@/lib/entitlements/getActiveEntitlement";
import { auth, db } from "@/lib/firebase";
import {
  canInitiateWithPaymentConfiguration,
  formatKesAmount,
} from "@/lib/listingPaymentConfiguration";
import {
  beginOwnedListingPaymentPolling,
  LISTING_PAYMENT_INITIATION_MESSAGES,
  malformedListingPaymentRecoveryState,
  processOneShotListingPaymentStatus,
  resolveListingPaymentInitiationError,
  runOwnedPaymentAuthenticationRefresh,
  stopOwnedListingPaymentPolling,
  type ListingPaymentOneShotStatusPath,
  withListingPaymentInitiationTimeout,
} from "@/lib/listingPaymentInitiationError";
import { persistLegalAcceptance } from "@/lib/legalAcceptance";
import {
  buildListingPaymentInitiationRequest,
  canConfirmTerminalPaymentRetry,
  canCreateNewListingPaymentKey,
  createCompletionSingleFlight,
  legacyCleanupFailurePolicy,
  runtimeSessionFromAuthoritativeRecovery,
  shouldClearLegacyListingPaymentSession,
  type AuthoritativePaymentRecoveryState,
} from "@/lib/listingPaymentIntent";
import { deriveCanonicalPaidBusinessId } from "@/lib/listingPaymentIdentifiers";
import {
  clearAllListingPaymentLocalState,
  clearLegacyListingPaymentSession,
  clearListingPaymentIntent,
  createPaidListingDraft,
  generateListingPaymentIdempotencyKey,
  getListingPaymentConfiguration,
  initiateListingPayment,
  isActiveListingPaymentStatus,
  isFailedListingPaymentStatus,
  isValidListingEntitlementId,
  ListingPaymentProtocolError,
  readListingPaymentIntent,
  recoverCurrentListingPayment,
  saveListingPaymentIntent,
} from "@/lib/listingPayments";
import {
  createListingPaymentPollingTimer,
  LISTING_PAYMENT_POLL_DELAYS_MS,
  LISTING_PAYMENT_POLL_WINDOW_MS,
  withPaymentCheckLoading,
} from "@/lib/listingPaymentPolling";
import {
  createDraftBusiness,
  readOwnerOnboardingStep,
  readOwnerLegalAcceptance,
  saveCurrentOwnerLegalAcceptance,
} from "@/lib/onboardingBusiness";
import {
  normalizeOwnerOnboardingStatus,
  resolveOwnerOnboardingResumePath,
} from "@/lib/onboardingResume";
import {
  resolveOwnerStartFlow,
  type OwnerStartFlow,
} from "@/lib/ownerStartDecision";
import {
  getPioneerSlotOnboardingDisposition,
  getOwnerPioneerSlot,
  isUsablePioneerSlot,
} from "@/lib/pioneerSlots";
import type { ListingEntitlement } from "@/types/listingEntitlement";
import type {
  ListingPaymentIntentReadResult,
  ListingPaymentConfigurationLoadState,
  ListingPaymentRuntimeSession,
  RecoverCurrentListingPaymentResponse,
} from "@/types/listingPayment";
import type { PioneerSlot } from "@/types/pioneerSlot";

type BusinessStatus =
  | "draft"
  | "pending"
  | "approved"
  | "rejected"
  | "changes_requested";

type OwnerBusiness = {
  id: string;
  name?: string;
  status?: string;
  isDeleted?: boolean;
  onboardingStep?: unknown;
  updatedAt?: unknown;
  createdAt?: unknown;
};

type OwnerStartFieldErrors = Partial<
  Record<"businessName" | "mobileNumber", string>
>;

function formatDate(value: unknown) {
  try {
    const maybeTimestamp = value as { toDate?: () => Date };

    const date =
      typeof maybeTimestamp?.toDate === "function"
        ? maybeTimestamp.toDate()
        : value instanceof Date
        ? value
        : null;

    if (!date) return "";

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function isGoogleSignedIn(user: User | null) {
  return !!user?.providerData?.some(
    (provider) => provider.providerId === "google.com"
  );
}

function cleanText(value: string) {
  return value.trim();
}

function isValidKenyanMobile(value: string) {
  const normalized = value.replace(/[\s()-]+/g, "");

  return (
    /^\+2547\d{8}$/.test(normalized) ||
    /^\+2541\d{8}$/.test(normalized) ||
    /^2547\d{8}$/.test(normalized) ||
    /^2541\d{8}$/.test(normalized) ||
    /^07\d{8}$/.test(normalized) ||
    /^01\d{8}$/.test(normalized)
  );
}

function cleanStatus(value: unknown) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function readNumber(
  data: Record<string, unknown>,
  keys: string[],
  fallback = 0
) {
  for (const key of keys) {
    const value = data[key];

    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }

  return fallback;
}

function toMillis(value: unknown): number {
  if (!value) return 0;

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (
    typeof value === "object" &&
    "toMillis" in value &&
    typeof (value as { toMillis?: unknown }).toMillis === "function"
  ) {
    return (value as { toMillis: () => number }).toMillis();
  }

  return 0;
}

function isEntitlementActive(data: Record<string, unknown>) {
  const status = cleanStatus(data.status);

  if (
    status &&
    !["active", "trialing", "paid", "approved", "enabled"].includes(status)
  ) {
    return false;
  }

  if (data.isActive === false || data.active === false) {
    return false;
  }

  return true;
}

function normalizeEntitlement(
  id: string,
  data: Record<string, unknown>
): ListingEntitlement | null {
  if (!isEntitlementActive(data)) {
    return null;
  }

  const maxBusinesses = readNumber(data, [
    "maxBusinesses",
    "businessLimit",
    "maxListings",
    "listingLimit",
  ]);

  const usedBusinesses = readNumber(data, [
    "usedBusinesses",
    "usedBusinessCount",
    "businessesUsed",
    "usedListings",
  ]);

  if (maxBusinesses <= 0) {
    return null;
  }

  if (usedBusinesses >= maxBusinesses) {
    return null;
  }

  return {
    id,
    ...(data as Omit<ListingEntitlement, "id">),
    maxBusinesses,
    usedBusinesses,
  } as ListingEntitlement;
}

async function getActiveEntitlementDirect(
  ownerId: string
): Promise<ListingEntitlement | null> {
  const canonicalEntitlement = await getActiveEntitlement(ownerId);

  if (canonicalEntitlement) {
    return canonicalEntitlement;
  }

  const canonicalOwnerEntitlements = await getDocs(
    query(
      collection(db, "listingEntitlements"),
      where("ownerId", "==", ownerId)
    )
  );

  for (const entitlementDocument of canonicalOwnerEntitlements.docs) {
    const entitlement = normalizeEntitlement(
      entitlementDocument.id,
      entitlementDocument.data() as Record<string, unknown>
    );

    if (entitlement) return entitlement;
  }

  const refs = [
    doc(db, "ownerEntitlements", ownerId),
    doc(db, "entitlements", ownerId),
    doc(db, "owners", ownerId, "entitlements", "active"),
  ];

  for (const refDoc of refs) {
    const snap = await getDoc(refDoc);

    if (!snap.exists()) {
      continue;
    }

    const entitlement = normalizeEntitlement(
      snap.id,
      snap.data() as Record<string, unknown>
    );

    if (entitlement) {
      return entitlement;
    }
  }

  return null;
}

async function getOwnerBusinesses(ownerId: string): Promise<OwnerBusiness[]> {
  const q = query(
    collection(db, "businesses"),
    where("ownerId", "==", ownerId)
  );

  const snap = await getDocs(q);

  return snap.docs
    .map((docSnap) => ({
      id: docSnap.id,
      ...(docSnap.data() as Omit<OwnerBusiness, "id">),
    }))
    .sort((a, b) => {
      const aMs = toMillis(a.updatedAt) || toMillis(a.createdAt);
      const bMs = toMillis(b.updatedAt) || toMillis(b.createdAt);

      return bMs - aMs;
    });
}

function getBestBusinessToContinue(businesses: OwnerBusiness[]) {
  return businesses.find((biz) => biz.status === "draft") || null;
}

type PaymentUiState =
  | "idle"
  | "initiating"
  | "polling"
  | "poll_timeout"
  | "failed"
  | "cancelled"
  | "timed_out"
  | "verification_required"
  | "review_required"
  | "creating_draft"
  | "draft_error"
  | "configuration_error"
  | "initiation_rejected"
  | "authentication_required"
  | "permission_denied"
  | "initiation_error";

const PAYMENT_INITIATION_TIMEOUT_MS = 30_000;

function paymentStateForTerminalStatus(
  status: "failed" | "cancelled" | "timed_out"
): PaymentUiState {
  return status;
}

function paymentMessageForState(state: PaymentUiState): string | null {
  switch (state) {
    case "initiating":
      return "Sending the M-Pesa payment request...";
    case "polling":
      return "Waiting for verified payment confirmation...";
    case "poll_timeout":
      return "M-Pesa confirmation is delayed. Automatic checking paused; your payment session is saved and you can check its status again.";
    case "failed":
      return "The payment was not completed. Start a new attempt only when you are ready.";
    case "cancelled":
      return "The payment was cancelled. Start a new attempt only when you are ready.";
    case "timed_out":
      return "The M-Pesa payment request expired. Start a new attempt only when you are ready.";
    case "verification_required":
      return "This payment needs manual review. Do not start another payment; contact support for help.";
    case "review_required":
      return "We could not safely verify this payment response. Do not start another payment; contact support for help.";
    case "creating_draft":
      return "Payment verified. Preparing your business profile...";
    case "draft_error":
      return "Your payment is verified, but the business profile could not be prepared. Retry to continue with the same entitlement.";
    case "configuration_error":
      return LISTING_PAYMENT_INITIATION_MESSAGES.configuration_error;
    case "initiation_rejected":
      return LISTING_PAYMENT_INITIATION_MESSAGES.initiation_rejected;
    case "authentication_required":
      return LISTING_PAYMENT_INITIATION_MESSAGES.authentication_required;
    case "permission_denied":
      return LISTING_PAYMENT_INITIATION_MESSAGES.permission_denied;
    case "initiation_error":
      return LISTING_PAYMENT_INITIATION_MESSAGES.initiation_error;
    default:
      return null;
  }
}

function paymentStateForSession(
  session: ListingPaymentRuntimeSession | null
): PaymentUiState {
  if (!session) return "idle";
  if (session.manualReviewRequired) return "review_required";
  if (!session.lastStatus) {
    return session.intent ? "initiation_error" : "review_required";
  }
  if (isActiveListingPaymentStatus(session.lastStatus)) return "poll_timeout";
  if (isFailedListingPaymentStatus(session.lastStatus)) {
    return paymentStateForTerminalStatus(session.lastStatus);
  }
  if (session.lastStatus === "verification_required") {
    return "verification_required";
  }
  if (session.lastStatus === "succeeded") {
    if (!isValidListingEntitlementId(session.entitlementId)) {
      return "review_required";
    }

    return "creating_draft";
  }

  return "review_required";
}

export default function OwnerStart() {
  const { authInitialized, user, ownerReady, ownerError } = useOwnerAuth();
  const insets = useSafeAreaInsets();
  const scrollRef = useRef<ScrollView | null>(null);
  const businessNameInputRef = useRef<TextInput | null>(null);
  const mobileNumberInputRef = useRef<TextInput | null>(null);
  const mountedRef = useRef(true);
  const activeUidRef = useRef<string | null>(null);
  const loadGenerationRef = useRef(0);
  const appActiveRef = useRef(AppState.currentState === "active");
  const paymentSessionRef = useRef<ListingPaymentRuntimeSession | null>(null);
  const paymentPollingTimerRef = useRef(createListingPaymentPollingTimer());
  const newPaymentLockRef = useRef(false);
  const initiationRequestRef = useRef<{
    uid: string;
    idempotencyKey: string;
    configurationFingerprint: string;
    promise: ReturnType<typeof initiateListingPayment>;
  } | null>(null);
  const statusRequestRef = useRef<{
    uid: string;
    promise: ReturnType<typeof recoverCurrentListingPayment>;
  } | null>(null);
  const completionSingleFlightRef = useRef(createCompletionSingleFlight());
  const ownerLoadRetryRef = useRef<Promise<void> | null>(null);

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [legalChecking, setLegalChecking] = useState(true);
  const [legalSaving, setLegalSaving] = useState(false);
  const [legalError, setLegalError] = useState<string | null>(null);
  const [hasCurrentLegalAcceptance, setHasCurrentLegalAcceptance] =
    useState(false);

  const [entitlement, setEntitlement] =
    useState<ListingEntitlement | null>(null);
  const [pioneerSlot, setPioneerSlot] = useState<PioneerSlot | null>(null);
  const [businesses, setBusinesses] = useState<OwnerBusiness[]>([]);
  const [ownerOnboardingStep, setOwnerOnboardingStep] =
    useState<unknown>(null);
  const [ownerLoadError, setOwnerLoadError] = useState<string | null>(null);
  const [ownerLoadRetryBusy, setOwnerLoadRetryBusy] = useState(false);
  const [sessionLoadError, setSessionLoadError] = useState<string | null>(null);
  const [localPrivacyWarning, setLocalPrivacyWarning] = useState<string | null>(
    null
  );
  const [paymentSession, setPaymentSession] =
    useState<ListingPaymentRuntimeSession | null>(null);
  const [paymentUiState, setPaymentUiState] =
    useState<PaymentUiState>("idle");
  const [paymentConfigurationState, setPaymentConfigurationState] =
    useState<ListingPaymentConfigurationLoadState>({
      status: "loading",
      configuration: null,
    });
  const [manualPaymentCheckBusy, setManualPaymentCheckBusy] = useState(false);
  const [authoritativeRecoveryState, setAuthoritativeRecoveryState] =
    useState<AuthoritativePaymentRecoveryState>("checking");
  const [terminalRetryEditing, setTerminalRetryEditing] = useState(false);

  const [ownerName, setOwnerName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("+254");
  const [fieldErrors, setFieldErrors] = useState<OwnerStartFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const visibleBusinesses = useMemo(
    () => businesses.filter((business) => business.isDeleted !== true),
    [businesses]
  );

  const businessToContinue = useMemo(
    () => getBestBusinessToContinue(visibleBusinesses),
    [visibleBusinesses]
  );

  const hasAnyBusiness = visibleBusinesses.length > 0;
  const hasAnyBusinessDocument = businesses.length > 0;
  const hasUsablePioneerSlot = isUsablePioneerSlot(pioneerSlot);
  const pioneerSlotDisposition = getPioneerSlotOnboardingDisposition(
    pioneerSlot
  );

  const remainingEntitlementSlots = entitlement
    ? Math.max(0, entitlement.maxBusinesses - entitlement.usedBusinesses)
    : 0;

  const hasEntitlementSlot = remainingEntitlementSlots > 0;

  const hasPendingBusiness = visibleBusinesses.some(
    (business) => business.status === "pending"
  );

  const hasApprovedBusiness = visibleBusinesses.some(
    (business) => business.status === "approved"
  );

  const hasFeedbackBusiness = visibleBusinesses.some(
    (business) =>
      business.status === "rejected" ||
      business.status === "changes_requested"
  );

  const hasLockedExistingBusiness = visibleBusinesses.some(
    (business) =>
      normalizeOwnerOnboardingStatus(business.status) !== "draft"
  );

  const hasBusinessRecoveryBlock =
    hasAnyBusinessDocument &&
    !businessToContinue &&
    !hasLockedExistingBusiness;

  const startFlow: OwnerStartFlow = resolveOwnerStartFlow({
    ownerLoadFailed: !!ownerLoadError,
    hasBusinessToContinue: !!businessToContinue,
    hasLockedExistingBusiness,
    hasAnyBusinessDocument,
    hasPaymentSession: !!paymentSession,
    hasEntitlementSlot,
    sessionLoadFailed: !!sessionLoadError,
    hasBusinessRecoveryBlock,
    pioneerSlotDisposition,
  });

  const legalBusy = legalChecking || legalSaving;

  const requiresLegalAcceptance =
    startFlow === "resume" ||
    startFlow === "entitlement" ||
    startFlow === "pioneer" ||
    (startFlow === "paid" &&
      (!paymentSession ||
        paymentSession.lastStatus === undefined ||
        isFailedListingPaymentStatus(paymentSession.lastStatus)));

  const listingCtaDisabled =
    legalBusy || !hasCurrentLegalAcceptance || creating;

  const gmailEmail = user?.email ?? "";

  const scrollToFormBottom = useCallback(() => {
    setTimeout(() => {
      scrollRef.current?.scrollToEnd({ animated: true });
    }, 280);
  }, []);

  const stopPaymentPolling = useCallback(() => {
    paymentPollingTimerRef.current.stop();
  }, []);

  const updatePaymentSession = useCallback(
    (nextSession: ListingPaymentRuntimeSession) => {
      if (
        mountedRef.current &&
        activeUidRef.current === nextSession.uid
      ) {
        paymentSessionRef.current = nextSession;
        setPaymentSession(nextSession);
      }
    },
    []
  );

  const markPaymentForManualReview = useCallback(
    async (session: ListingPaymentRuntimeSession) => {
      updatePaymentSession({
        ...session,
        manualReviewRequired: true,
      });

      if (
        mountedRef.current &&
        activeUidRef.current === session.uid
      ) {
        setPaymentUiState("review_required");
      }
    },
    [updatePaymentSession]
  );

  const completePaidListing = useCallback(
    (args: {
      uid: string;
      entitlementId?: string;
      consumedBusinessId?: string;
    }) =>
      completionSingleFlightRef.current.run(async () => {
        if (
          !mountedRef.current ||
          !appActiveRef.current ||
          activeUidRef.current !== args.uid ||
          auth.currentUser?.uid !== args.uid
        ) {
          throw new Error("Paid listing completion paused.");
        }

        setPaymentUiState("creating_draft");

        if (!isValidListingEntitlementId(args.entitlementId)) {
          throw new ListingPaymentProtocolError(
            "The verified listing entitlement requires review."
          );
        }

        let businessId = args.consumedBusinessId?.trim() ?? "";
        if (!businessId) {
          const result = await createPaidListingDraft({
            entitlementId: args.entitlementId,
            ownerName: "",
            businessName: "",
            mobileNumber: "",
          });
          businessId = result.businessId;
        }
        if (
          businessId !== deriveCanonicalPaidBusinessId(args.entitlementId)
        ) {
          throw new ListingPaymentProtocolError(
            "The paid business draft response requires review."
          );
        }

        if (
          !mountedRef.current ||
          !appActiveRef.current ||
          activeUidRef.current !== args.uid ||
          auth.currentUser?.uid !== args.uid
        ) {
          throw new Error("Paid listing completion paused.");
        }

        stopPaymentPolling();
        router.replace({
          pathname: "/(owner)/onboarding/basics",
          params: { businessId },
        } as any);

        // Navigation is safe before cleanup because server recovery now returns
        // the consumed business. Cleanup failure must not cause a second route.
        await clearAllListingPaymentLocalState(args.uid).catch(() => undefined);

        if (activeUidRef.current === args.uid) {
          paymentSessionRef.current = null;
          setPaymentSession(null);
          setPaymentUiState("idle");
        }
      }),
    [stopPaymentPolling]
  );

  const loadOwnerState = useCallback(async (ownerId: string) => {
    const [
      activeEntitlement,
      ownerBusinesses,
      ownerPioneerSlot,
      ownerLegalAcceptance,
      savedOwnerOnboardingStep,
    ] = await Promise.all([
      getActiveEntitlementDirect(ownerId),
      getOwnerBusinesses(ownerId),
      getOwnerPioneerSlot(ownerId),
      readOwnerLegalAcceptance(ownerId),
      readOwnerOnboardingStep(ownerId),
    ]);

    return {
      activeEntitlement,
      ownerBusinesses,
      ownerPioneerSlot,
      ownerLegalAcceptance,
      savedOwnerOnboardingStep,
    };
  }, []);

  const loadAuthenticatedState = useCallback(
    async (nextUser: User) => {
      const generation = ++loadGenerationRef.current;
      const ownerId = nextUser.uid;

      stopPaymentPolling();
      activeUidRef.current = ownerId;
      paymentSessionRef.current = null;
      setCreating(false);
      setManualPaymentCheckBusy(false);
      setPaymentSession(null);
      setPaymentUiState("idle");
      setPaymentConfigurationState({
        status: "loading",
        configuration: null,
      });
      setAuthoritativeRecoveryState("checking");
      setTerminalRetryEditing(false);
      setOwnerLoadError(null);
      setSessionLoadError(null);
      setLocalPrivacyWarning(null);
      setLegalChecking(true);
      setLoading(true);

      const [
        ownerResult,
        sessionResult,
        recoveryResult,
        paymentConfigurationResult,
      ] =
        await Promise.allSettled([
        loadOwnerState(ownerId),
        readListingPaymentIntent(ownerId),
        recoverCurrentListingPayment(),
        getListingPaymentConfiguration(),
      ]);

      if (
        !mountedRef.current ||
        generation !== loadGenerationRef.current ||
        activeUidRef.current !== ownerId
      ) {
        return;
      }

      let recoveredSession: ListingPaymentRuntimeSession | null = null;
      let paymentRecoveryBlocked = false;

      if (paymentConfigurationResult.status === "fulfilled") {
        setPaymentConfigurationState({
          status: "ready",
          configuration: paymentConfigurationResult.value,
        });
      } else {
        setPaymentConfigurationState({
          status: "error",
          configuration: null,
        });
      }

      if (recoveryResult.status === "fulfilled") {
        setAuthoritativeRecoveryState(
          recoveryResult.value.status === "none" ? "none" : "present"
        );

        const localIntentResult: ListingPaymentIntentReadResult =
          sessionResult.status === "fulfilled"
            ? sessionResult.value
            : {
                status: "malformed",
                intent: null,
                legacySessionPresent: false,
              };

        if (sessionResult.status === "fulfilled") {
          try {
            if (
              localIntentResult.legacySessionPresent &&
              shouldClearLegacyListingPaymentSession(true)
            ) {
              await clearLegacyListingPaymentSession(ownerId);
            }
            if (
              localIntentResult.status === "expired" ||
              localIntentResult.status === "malformed"
            ) {
              await clearListingPaymentIntent(ownerId);
            }
          } catch {
            const cleanupPolicy = legacyCleanupFailurePolicy(
              recoveryResult.value.status
            );
            paymentRecoveryBlocked = cleanupPolicy.blocksNewPayment;
            if (cleanupPolicy.blocksNewPayment) {
              setSessionLoadError(
                "We recovered the server payment state but could not safely clean old local payment data. Retry before starting a payment."
              );
            } else if (cleanupPolicy.showPrivacyWarning) {
              setLocalPrivacyWarning(
                "Your server payment was recovered, but old local payment data could not be removed from this device. The recovered payment remains authoritative."
              );
            }
          }
        } else if (recoveryResult.value.status === "none") {
          paymentRecoveryBlocked = true;
          setSessionLoadError(
            "We recovered the server payment state but could not read local payment intent storage. Retry before starting a payment."
          );
        } else {
          setLocalPrivacyWarning(
            "Your server payment was recovered, but local payment storage could not be checked on this device. The recovered payment remains authoritative."
          );
        }

        if (recoveryResult.value.status === "consumed") {
          setLegalChecking(false);
          setLoading(false);
          await completePaidListing({
            uid: ownerId,
            entitlementId: recoveryResult.value.entitlementId,
            consumedBusinessId: recoveryResult.value.businessId,
          }).catch(() => {
            if (mountedRef.current && activeUidRef.current === ownerId) {
              setPaymentUiState("draft_error");
            }
          });
          return;
        }

        const resolved = runtimeSessionFromAuthoritativeRecovery({
          uid: ownerId,
          localIntent:
            localIntentResult.status === "valid"
              ? localIntentResult.intent
              : null,
          recovery: recoveryResult.value,
        });

        recoveredSession = resolved.session;
        paymentSessionRef.current = recoveredSession;
        setPaymentSession(recoveredSession);
        setPaymentUiState(paymentStateForSession(recoveredSession));

        paymentRecoveryBlocked =
          paymentRecoveryBlocked || resolved.requiresReview;
        if (paymentRecoveryBlocked) {
          setSessionLoadError((current) =>
            current ??
            "The server payment state requires review. Do not start another payment; retry or contact support."
          );
        }
      } else {
        setAuthoritativeRecoveryState("error");
        setSessionLoadError(
          "We could not check the server payment state. Retry before starting or repeating a payment."
        );
      }

      if (ownerResult.status === "fulfilled") {
        const ownerState = ownerResult.value;

        setEntitlement(ownerState.activeEntitlement);
        setBusinesses(ownerState.ownerBusinesses);
        setPioneerSlot(ownerState.ownerPioneerSlot);
        // readOwnerLegalAcceptance returns a value only after the complete
        // identifier, revision-token, exact-key, and timestamp predicate passes.
        setHasCurrentLegalAcceptance(
          ownerState.ownerLegalAcceptance !== null
        );
        setOwnerOnboardingStep(ownerState.savedOwnerOnboardingStep);
        setLegalError(null);

      } else {
        setOwnerLoadError(
          "We could not load all business and entitlement records. Retry before continuing."
        );
      }

      setLegalChecking(false);
      setLoading(false);

      if (
        recoveredSession?.lastStatus === "succeeded" &&
        isValidListingEntitlementId(recoveredSession.entitlementId) &&
        activeUidRef.current === ownerId &&
        appActiveRef.current
      ) {
        void completePaidListing({
          uid: ownerId,
          entitlementId: recoveredSession.entitlementId,
        }).catch(() => {
          if (mountedRef.current && activeUidRef.current === ownerId) {
            setPaymentUiState("draft_error");
          }
        });
      }
    },
    [
      completePaidListing,
      loadOwnerState,
      stopPaymentPolling,
    ]
  );

  useEffect(() => {
    mountedRef.current = true;
    const completionSingleFlight = completionSingleFlightRef.current;

    return () => {
      mountedRef.current = false;
      activeUidRef.current = null;
      loadGenerationRef.current += 1;
      stopPaymentPolling();
      completionSingleFlight.reset();
    };
  }, [stopPaymentPolling]);

  useEffect(() => {
    if (!authInitialized) return;

    const completionSingleFlight = completionSingleFlightRef.current;
    const nextUser = user;
      const authenticatedUidChanged =
        activeUidRef.current !== (nextUser?.uid ?? null);
      loadGenerationRef.current += 1;
      stopPaymentPolling();
      newPaymentLockRef.current = false;
      initiationRequestRef.current = null;
      statusRequestRef.current = null;
      completionSingleFlight.reset();
      if (authenticatedUidChanged) {
        ownerLoadRetryRef.current = null;
        setOwnerLoadRetryBusy(false);
        setCreating(false);
        setManualPaymentCheckBusy(false);
      }
      setAuthoritativeRecoveryState("checking");
      setTerminalRetryEditing(false);

      if (!nextUser) {
        activeUidRef.current = null;
        paymentSessionRef.current = null;
        setPaymentSession(null);
        setPaymentConfigurationState({
          status: "loading",
          configuration: null,
        });
        setAuthoritativeRecoveryState("error");
        setHasCurrentLegalAcceptance(false);
        setOwnerOnboardingStep(null);
        setLegalError(null);
        setLegalChecking(false);
        setLoading(false);
        router.replace("/(owner)/onboarding/signin" as any);
        return;
      }

      if (!ownerReady) return;

      activeUidRef.current = nextUser.uid;
      setOwnerName(nextUser.displayName ?? "");
      setBusinessName("");
      setMobileNumber("+254");
      setEntitlement(null);
      setBusinesses([]);
      setPioneerSlot(null);
      setPaymentConfigurationState({
        status: "loading",
        configuration: null,
      });
      setHasCurrentLegalAcceptance(false);
      setOwnerOnboardingStep(null);
      setLegalError(null);
      if (!ownerLoadRetryRef.current) {
        void loadAuthenticatedState(nextUser);
      }
  }, [
    authInitialized,
    user,
    ownerReady,
    loadAuthenticatedState,
    stopPaymentPolling,
  ]);

  useEffect(() => {
    const subscription = AppState.addEventListener("change", (nextState) => {
      const nextIsActive = nextState === "active";

      appActiveRef.current = nextIsActive;

      if (!nextIsActive) {
        const currentSession = paymentSessionRef.current;
        stopOwnedListingPaymentPolling({
          pollingTimer: paymentPollingTimerRef.current,
          activePayment:
            !!currentSession?.lastStatus &&
            isActiveListingPaymentStatus(currentSession.lastStatus),
          onActiveStopped: ({ uiState }) => setPaymentUiState(uiState),
        });
      }
    });

    return () => subscription.remove();
  }, []);

  useFocusEffect(
    useCallback(
      () => () => {
        const currentSession = paymentSessionRef.current;
        stopOwnedListingPaymentPolling({
          pollingTimer: paymentPollingTimerRef.current,
          activePayment:
            !!currentSession?.lastStatus &&
            isActiveListingPaymentStatus(currentSession.lastStatus),
          onActiveStopped: ({ uiState }) => setPaymentUiState(uiState),
        });
        setManualPaymentCheckBusy(false);
      },
      []
    )
  );

  const requireCurrentLegalAcceptance = useCallback(() => {
    if (legalBusy) {
      return false;
    }

    if (!hasCurrentLegalAcceptance) {
      setLegalError("Accept the legal policies before continuing.");
      return false;
    }

    return true;
  }, [hasCurrentLegalAcceptance, legalBusy]);

  const reloadPaymentConfiguration = useCallback(async (ownerId: string) => {
    if (
      !ownerId ||
      activeUidRef.current !== ownerId ||
      auth.currentUser?.uid !== ownerId
    ) {
      return false;
    }

    setPaymentConfigurationState({
      status: "loading",
      configuration: null,
    });

    try {
      const configuration = await getListingPaymentConfiguration();
      if (
        !mountedRef.current ||
        activeUidRef.current !== ownerId ||
        auth.currentUser?.uid !== ownerId
      ) {
        return false;
      }

      setPaymentConfigurationState({
        status: "ready",
        configuration,
      });
      return true;
    } catch {
      if (
        mountedRef.current &&
        activeUidRef.current === ownerId &&
        auth.currentUser?.uid === ownerId
      ) {
        setPaymentConfigurationState({
          status: "error",
          configuration: null,
        });
      }
      return false;
    }
  }, []);

  const continueExistingBusiness = useCallback(() => {
    if (!businessToContinue?.id) return;

    if (!requireCurrentLegalAcceptance()) return;

    const pathname = resolveOwnerOnboardingResumePath({
      status: businessToContinue.status as BusinessStatus,
      onboardingStep: businessToContinue.onboardingStep,
      ownerOnboardingStep,
    });

    if (pathname === "/(owner)/dashboard") {
      router.push(pathname as any);
      return;
    }

    router.push({
      pathname,
      params: { businessId: businessToContinue.id },
    } as any);
  }, [
    businessToContinue,
    ownerOnboardingStep,
    requireCurrentLegalAcceptance,
  ]);

  const acceptLegalTerms = useCallback(
    async (accepted: boolean) => {
      if (legalSaving) {
        return;
      }

      if (!accepted) {
        setHasCurrentLegalAcceptance(false);
        setLegalError(null);
        return;
      }

      if (!user || hasCurrentLegalAcceptance) return;

      setLegalSaving(true);
      setLegalError(null);

      try {
        const persisted = await persistLegalAcceptance(() =>
          saveCurrentOwnerLegalAcceptance(user.uid)
        );
        if (!persisted) {
          setHasCurrentLegalAcceptance(false);
          setLegalError(
            "We could not save your legal acceptance. Please check your connection and try again."
          );
          return;
        }

        if (
          mountedRef.current &&
          activeUidRef.current === user.uid &&
          auth.currentUser?.uid === user.uid
        ) {
          setHasCurrentLegalAcceptance(true);
        }
      } finally {
        setLegalSaving(false);
      }
    },
    [hasCurrentLegalAcceptance, legalSaving, user]
  );

  const validateForm = useCallback(() => {
    let nextFormError: string | null = null;

    if (!user) {
      nextFormError = "Please sign in with Google first.";
    } else if (!isGoogleSignedIn(user)) {
      nextFormError = "Please sign in with Google to continue.";
    }

    const nextFieldErrors: OwnerStartFieldErrors = {};

    if (!cleanText(businessName)) {
      nextFieldErrors.businessName = "Enter your business name.";
    }

    if (!isValidKenyanMobile(cleanText(mobileNumber))) {
      nextFieldErrors.mobileNumber =
        "Enter a valid Kenyan M-Pesa mobile number.";
    }

    setFormError(nextFormError);
    setFieldErrors(nextFieldErrors);

    const firstInvalidInput = nextFieldErrors.businessName
      ? businessNameInputRef.current
      : nextFieldErrors.mobileNumber
      ? mobileNumberInputRef.current
      : null;

    if (firstInvalidInput) {
      setTimeout(() => firstInvalidInput.focus(), 0);
    }

    return !nextFormError && Object.keys(nextFieldErrors).length === 0;
  }, [businessName, mobileNumber, user]);

  const getInitiationRequest = useCallback(
    (session: ListingPaymentRuntimeSession) => {
      if (!session.intent || !session.phoneNumber) {
        throw new ListingPaymentProtocolError(
          "The saved payment intent requires a phone number before retrying."
        );
      }

      const configuration =
        paymentConfigurationState.status === "ready"
          ? paymentConfigurationState.configuration
          : null;
      if (!configuration) {
        throw new ListingPaymentProtocolError(
          "Reload payment details before trying again."
        );
      }

      const current = initiationRequestRef.current;

      if (
        current &&
        current.uid === session.uid &&
        current.idempotencyKey === session.intent.idempotencyKey &&
        current.configurationFingerprint ===
          configuration.configurationFingerprint
      ) {
        return current.promise;
      }

      const initiationRequest = buildListingPaymentInitiationRequest({
        intent: session.intent,
        phoneNumber: session.phoneNumber,
        configurationVersion: configuration.configurationVersion,
        configurationFingerprint:
          configuration.configurationFingerprint,
      });
      const promise = initiateListingPayment(initiationRequest);
      const request = {
        uid: session.uid,
        idempotencyKey: session.intent.idempotencyKey,
        configurationFingerprint:
          configuration.configurationFingerprint,
        promise,
      };

      initiationRequestRef.current = request;
      promise.then(
        () => {
          if (initiationRequestRef.current === request) {
            initiationRequestRef.current = null;
          }
        },
        () => {
          if (initiationRequestRef.current === request) {
            initiationRequestRef.current = null;
          }
        }
      );

      return promise;
    },
    [paymentConfigurationState]
  );

  const getStatusRequest = useCallback(
    (uid: string) => {
      const current = statusRequestRef.current;

      if (current && current.uid === uid) {
        return current.promise;
      }

      const promise = recoverCurrentListingPayment();
      const request = { uid, promise };

      statusRequestRef.current = request;
      promise.then(
        () => {
          if (statusRequestRef.current === request) {
            statusRequestRef.current = null;
          }
        },
        () => {
          if (statusRequestRef.current === request) {
            statusRequestRef.current = null;
          }
        }
      );

      return promise;
    },
    []
  );

  const processPaymentStatus = useCallback(
    async (
      session: ListingPaymentRuntimeSession,
      response: RecoverCurrentListingPaymentResponse
    ): Promise<ListingPaymentRuntimeSession | null> => {
      if (
        activeUidRef.current !== session.uid ||
        auth.currentUser?.uid !== session.uid
      ) {
        return null;
      }

      if (response.status === "none") {
        setAuthoritativeRecoveryState("none");
        if (!session.lastStatus && session.intent) {
          setPaymentUiState("initiation_error");
        } else {
          await markPaymentForManualReview(session);
        }
        return null;
      }

      if (response.status === "consumed") {
        setAuthoritativeRecoveryState("present");
        await completePaidListing({
          uid: session.uid,
          entitlementId: response.entitlementId,
          consumedBusinessId: response.businessId,
        });
        return null;
      }

      setAuthoritativeRecoveryState("present");

      if (response.status === "verification_required" && !response.payment) {
        await markPaymentForManualReview(session);
        return null;
      }

      const payment = response.payment;
      if (!payment) {
        await markPaymentForManualReview(session);
        return null;
      }

      const validEntitlementId = isValidListingEntitlementId(
        payment.entitlementId
      )
        ? payment.entitlementId
        : undefined;
      const nextSession: ListingPaymentRuntimeSession = {
        ...session,
        paymentId: payment.paymentId,
        lastStatus: response.status,
        entitlementId: validEntitlementId ?? session.entitlementId,
        maskedPhone: payment.maskedPhone,
      };

      updatePaymentSession(nextSession);

      if (isActiveListingPaymentStatus(response.status)) {
        return nextSession;
      }

      if (response.status === "succeeded") {
        if (!validEntitlementId) {
          await markPaymentForManualReview(nextSession);
          return null;
        }

        if (!appActiveRef.current) {
          setPaymentUiState("draft_error");
          return null;
        }

        try {
          await completePaidListing({
            uid: nextSession.uid,
            entitlementId: validEntitlementId,
          });
        } catch {
          setPaymentUiState("draft_error");
        }
        return null;
      }

      if (isFailedListingPaymentStatus(response.status)) {
        setTerminalRetryEditing(false);
        setPaymentUiState(paymentStateForTerminalStatus(response.status));
        return null;
      }

      if (response.status === "verification_required") {
        setTerminalRetryEditing(false);
        setPaymentUiState("verification_required");
        return null;
      }

      await markPaymentForManualReview(nextSession);
      return null;
    },
    [
      completePaidListing,
      markPaymentForManualReview,
      updatePaymentSession,
    ]
  );

  const processOneShotPaymentStatus = useCallback(
    (
      path: ListingPaymentOneShotStatusPath,
      session: ListingPaymentRuntimeSession,
      response: RecoverCurrentListingPaymentResponse
    ) =>
      processOneShotListingPaymentStatus({
        path,
        processStatus: () => processPaymentStatus(session, response),
        onActive: ({ uiState }) => setPaymentUiState(uiState),
      }),
    [processPaymentStatus]
  );

  const beginPaymentPolling = useCallback(
    (initialSession: ListingPaymentRuntimeSession) => {
      const pollingTimer = paymentPollingTimerRef.current;
      const run = beginOwnedListingPaymentPolling({
        pollingTimer,
        windowMs: LISTING_PAYMENT_POLL_WINDOW_MS,
        onTimeout: () => {
          if (
            mountedRef.current &&
            appActiveRef.current &&
            activeUidRef.current === initialSession.uid
          ) {
            setPaymentUiState("poll_timeout");
          }
        },
        onPolling: ({ uiState }) => setPaymentUiState(uiState),
      });

      const scheduleNext = (
        session: ListingPaymentRuntimeSession,
        attempt: number
      ) => {
        if (
          !mountedRef.current ||
          !appActiveRef.current ||
          activeUidRef.current !== session.uid ||
          !pollingTimer.isCurrent(run)
        ) {
          return;
        }

        const delay =
          LISTING_PAYMENT_POLL_DELAYS_MS[
            Math.min(attempt, LISTING_PAYMENT_POLL_DELAYS_MS.length - 1)
          ];

        pollingTimer.schedule(run, delay, () => {
          void (async () => {
            if (
              !mountedRef.current ||
              !appActiveRef.current ||
              activeUidRef.current !== session.uid ||
              !pollingTimer.isCurrent(run)
            ) {
              return;
            }

            try {
              const response = await getStatusRequest(session.uid);

              if (
                !mountedRef.current ||
                !appActiveRef.current ||
                activeUidRef.current !== session.uid ||
                !pollingTimer.isCurrent(run)
              ) {
                return;
              }

              const nextSession = await processPaymentStatus(session, response);

              if (nextSession) {
                scheduleNext(nextSession, attempt + 1);
              } else {
                pollingTimer.stop();
              }
            } catch {
              if (
                !mountedRef.current ||
                !appActiveRef.current ||
                activeUidRef.current !== session.uid ||
                !pollingTimer.isCurrent(run)
              ) {
                return;
              }

              scheduleNext(session, attempt + 1);
            }
          })();
        });
      };

      scheduleNext(initialSession, 0);
    },
    [
      getStatusRequest,
      processPaymentStatus,
    ]
  );

  const refreshPaymentSession = useCallback(
    async (session: ListingPaymentRuntimeSession) => {
      if (
        activeUidRef.current !== session.uid ||
        auth.currentUser?.uid !== session.uid ||
        !appActiveRef.current
      ) {
        return;
      }

      stopPaymentPolling();

      try {
        const response = await getStatusRequest(session.uid);

        if (
          !mountedRef.current ||
          !appActiveRef.current ||
          activeUidRef.current !== session.uid
        ) {
          return;
        }

        await processOneShotPaymentStatus(
          "refresh_payment_session",
          session,
          response
        );
      } catch {
        if (
          mountedRef.current &&
          activeUidRef.current === session.uid
        ) {
          setPaymentUiState("poll_timeout");
        }
      }
    },
    [
      getStatusRequest,
      processOneShotPaymentStatus,
      stopPaymentPolling,
    ]
  );

  const initiateSavedPayment = useCallback(
    async (session: ListingPaymentRuntimeSession) => {
      if (
        activeUidRef.current !== session.uid ||
        auth.currentUser?.uid !== session.uid
      ) {
        return;
      }

      if (!appActiveRef.current) {
        setPaymentUiState("initiation_error");
        return;
      }

      setCreating(true);
      setPaymentUiState("initiating");

      try {
        const response = await withListingPaymentInitiationTimeout(
          getInitiationRequest(session),
          PAYMENT_INITIATION_TIMEOUT_MS
        );

        if (
          !mountedRef.current ||
          activeUidRef.current !== session.uid ||
          auth.currentUser?.uid !== session.uid
        ) {
          return;
        }

        const nextSession: ListingPaymentRuntimeSession = {
          ...session,
          paymentId: response.paymentId,
          lastStatus: response.status,
        };

        setAuthoritativeRecoveryState("present");
        updatePaymentSession(nextSession);

        if (isActiveListingPaymentStatus(response.status)) {
          if (appActiveRef.current) {
            beginPaymentPolling(nextSession);
          }
          return;
        }

        if (response.status === "succeeded") {
          await refreshPaymentSession(nextSession);
          return;
        }

        if (isFailedListingPaymentStatus(response.status)) {
          setTerminalRetryEditing(false);
          setPaymentUiState(paymentStateForTerminalStatus(response.status));
          return;
        }

        if (response.status === "verification_required") {
          setTerminalRetryEditing(false);
          setPaymentUiState("verification_required");
          return;
        }

        await markPaymentForManualReview(nextSession);
      } catch (error) {
        if (
          mountedRef.current &&
          activeUidRef.current === session.uid
        ) {
          const resolution = resolveListingPaymentInitiationError(
            error,
            session
          );

          if (resolution.action === "reload_configuration") {
            setPaymentUiState(resolution.uiState);
            const reloaded = await reloadPaymentConfiguration(
              resolution.session.uid
            );
            if (
              mountedRef.current &&
              activeUidRef.current === resolution.session.uid
            ) {
              setFormError(
                reloaded
                  ? "Payment details changed. Review the refreshed amount before trying again."
                  : "Payment details changed and could not be reloaded. Retry loading payment details."
              );
            }
          } else if (resolution.action === "stop_for_user_action") {
            setPaymentUiState(resolution.uiState);
          } else {
            try {
              const recovery = await getStatusRequest(resolution.session.uid);
              if (
                mountedRef.current &&
                activeUidRef.current === resolution.session.uid &&
                auth.currentUser?.uid === resolution.session.uid
              ) {
                await processOneShotPaymentStatus(
                  "ambiguous_initiation_recovery",
                  resolution.session,
                  recovery
                );
              }
            } catch {
              if (
                mountedRef.current &&
                activeUidRef.current === resolution.session.uid
              ) {
                const malformedRecovery =
                  malformedListingPaymentRecoveryState();
                setAuthoritativeRecoveryState("error");
                setPaymentUiState(malformedRecovery.uiState);
              }
            }
          }
        }
      } finally {
        if (
          mountedRef.current &&
          activeUidRef.current === session.uid
        ) {
          setCreating(false);
        }
      }
    },
    [
      beginPaymentPolling,
      getStatusRequest,
      getInitiationRequest,
      markPaymentForManualReview,
      processOneShotPaymentStatus,
      reloadPaymentConfiguration,
      refreshPaymentSession,
      updatePaymentSession,
    ]
  );

  const refreshPaymentAuthentication = useCallback(async () => {
    const session = paymentSessionRef.current;
    if (
      !session ||
      newPaymentLockRef.current ||
      activeUidRef.current !== session.uid
    ) {
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.uid !== session.uid) {
      router.replace("/(owner)/onboarding/signin" as any);
      return;
    }

    newPaymentLockRef.current = true;
    setCreating(true);
    setFormError(null);

    const generation = loadGenerationRef.current;
    const isOwnerCurrent = () =>
      mountedRef.current &&
      loadGenerationRef.current === generation &&
      activeUidRef.current === session.uid &&
      auth.currentUser?.uid === session.uid;

    await runOwnedPaymentAuthenticationRefresh({
      refreshToken: () => currentUser.getIdToken(true),
      isOwnerCurrent,
      onSuccess: () => {
        setPaymentUiState("initiation_rejected");
        setFormError(
          "Your sign-in was refreshed. Check the saved payment request when you are ready."
        );
      },
      onFailure: () => {
        setPaymentUiState("authentication_required");
        setFormError(
          "Your sign-in could not be refreshed. Sign out and sign in again before checking this saved payment request."
        );
      },
      onSettled: () => {
        newPaymentLockRef.current = false;
        setCreating(false);
      },
    });
  }, []);

  const confirmPaidEligibility = useCallback(
    async (
      ownerId: string
    ): Promise<"paid" | "existing" | "error"> => {
      try {
        const [
          ownerBusinesses,
          activeEntitlement,
          ownerPioneerSlot,
        ] = await Promise.all([
          getOwnerBusinesses(ownerId),
          getActiveEntitlementDirect(ownerId),
          getOwnerPioneerSlot(ownerId),
        ]);

        if (activeUidRef.current !== ownerId) return "error";

        setBusinesses(ownerBusinesses);
        setEntitlement(activeEntitlement);
        setPioneerSlot(ownerPioneerSlot);
        setOwnerLoadError(null);

        const slotDisposition = getPioneerSlotOnboardingDisposition(
          ownerPioneerSlot
        );
        if (
          ownerBusinesses.length > 0 ||
          activeEntitlement ||
          slotDisposition === "usable" ||
          slotDisposition === "used" ||
          slotDisposition === "recovery"
        ) {
          return "existing";
        }

        if (
          slotDisposition === "terminal" ||
          slotDisposition === "absent"
        ) {
          return "paid";
        }
      } catch {
        if (activeUidRef.current === ownerId) {
          setOwnerLoadError(
            "We could not recheck all business and entitlement records. Retry before paying."
          );
        }
        return "error";
      }

      return "error";
    },
    []
  );

  const startPaidPayment = useCallback(async () => {
    if (
      !user ||
      paymentSessionRef.current ||
      !canCreateNewListingPaymentKey({
        recoveryState: authoritativeRecoveryState,
        hasRuntimeSession: !!paymentSessionRef.current,
      }) ||
      newPaymentLockRef.current ||
      creating ||
      legalBusy ||
      !canInitiateWithPaymentConfiguration(paymentConfigurationState)
    ) {
      return;
    }

    const formIsValid = validateForm();
    const legalIsAccepted = requireCurrentLegalAcceptance();
    if (!formIsValid || !legalIsAccepted) return;

    newPaymentLockRef.current = true;
    setCreating(true);
    setFormError(null);
    setSessionLoadError(null);
    let serverConfirmedNone = false;

    try {
      const recovery = await getStatusRequest(user.uid);

      if (recovery.status !== "none") {
        setAuthoritativeRecoveryState("present");
        const resolved = runtimeSessionFromAuthoritativeRecovery({
          uid: user.uid,
          localIntent: null,
          recovery,
        });
        if (resolved.session) {
          updatePaymentSession(resolved.session);
          await processOneShotPaymentStatus(
            "start_paid_payment_recovery",
            resolved.session,
            recovery
          );
        } else {
          setSessionLoadError(
            "The server payment state requires review. Retry or contact support."
          );
        }
        return;
      }

      serverConfirmedNone = true;
      setAuthoritativeRecoveryState("none");

      const eligibility = await confirmPaidEligibility(user.uid);

      if (eligibility !== "paid") {
        return;
      }

      if (
        activeUidRef.current !== user.uid ||
        !appActiveRef.current
      ) {
        return;
      }

      const idempotencyKey = generateListingPaymentIdempotencyKey();
      const intent = await saveListingPaymentIntent({
        uid: user.uid,
        idempotencyKey,
      });
      const session: ListingPaymentRuntimeSession = {
        uid: user.uid,
        intent,
        phoneNumber: cleanText(mobileNumber),
      };

      setPaymentUiState("initiating");
      updatePaymentSession(session);
      await initiateSavedPayment(session);
    } catch {
      if (activeUidRef.current === user.uid) {
        if (serverConfirmedNone) {
          setFormError(
            "We could not safely save the payment intent. No payment request was sent. Please retry."
          );
        } else {
          setAuthoritativeRecoveryState("error");
          setSessionLoadError(
            "We could not check the server payment state. Retry before starting a payment."
          );
        }
        setPaymentUiState("idle");
      }
    } finally {
      newPaymentLockRef.current = false;
      if (activeUidRef.current === user.uid) {
        setCreating(false);
      }
    }
  }, [
    authoritativeRecoveryState,
    confirmPaidEligibility,
    creating,
    getStatusRequest,
    initiateSavedPayment,
    legalBusy,
    mobileNumber,
    paymentConfigurationState,
    processOneShotPaymentStatus,
    requireCurrentLegalAcceptance,
    updatePaymentSession,
    user,
    validateForm,
  ]);

  const prepareTerminalRetry = useCallback(async () => {
    const session = paymentSessionRef.current;
    if (
      !session?.lastStatus ||
      !isFailedListingPaymentStatus(session.lastStatus) ||
      newPaymentLockRef.current ||
      activeUidRef.current !== session.uid
    ) {
      return;
    }

    newPaymentLockRef.current = true;
    setCreating(true);
    setSessionLoadError(null);

    try {
      const recovery = await getStatusRequest(session.uid);
      if (
        recovery.status === "failed" ||
        recovery.status === "cancelled" ||
        recovery.status === "timed_out"
      ) {
        setAuthoritativeRecoveryState("present");
        await processOneShotPaymentStatus(
          "prepare_terminal_retry",
          session,
          recovery
        );
        setTerminalRetryEditing(true);
        setTimeout(() => mobileNumberInputRef.current?.focus(), 0);
        return;
      }

      await processOneShotPaymentStatus(
        "prepare_terminal_retry",
        session,
        recovery
      );
    } catch {
      if (mountedRef.current && activeUidRef.current === session.uid) {
        setAuthoritativeRecoveryState("error");
        setSessionLoadError(
          "We could not refresh the terminal payment state. Retry before starting another payment."
        );
      }
    } finally {
      newPaymentLockRef.current = false;
      if (activeUidRef.current === session.uid) setCreating(false);
    }
  }, [getStatusRequest, processOneShotPaymentStatus]);

  const retryTerminalPayment = useCallback(async () => {
    const session = paymentSessionRef.current;

    if (
      !session ||
      !session.lastStatus ||
      !isFailedListingPaymentStatus(session.lastStatus) ||
      !terminalRetryEditing ||
      newPaymentLockRef.current ||
      activeUidRef.current !== session.uid ||
      !canInitiateWithPaymentConfiguration(paymentConfigurationState)
    ) {
      return;
    }

    const formIsValid = validateForm();
    const legalIsAccepted = requireCurrentLegalAcceptance();
    if (!formIsValid || !legalIsAccepted) return;

    newPaymentLockRef.current = true;
    setCreating(true);
    setSessionLoadError(null);

    try {
      const recovery = await getStatusRequest(session.uid);
      if (
        !canConfirmTerminalPaymentRetry({
          authoritativeStatus: recovery.status,
          userConfirmed: true,
          phoneNumberIsValid: isValidKenyanMobile(cleanText(mobileNumber)),
        })
      ) {
        await processOneShotPaymentStatus(
          "retry_terminal_payment",
          session,
          recovery
        );
        return;
      }

      setAuthoritativeRecoveryState("present");
      const eligibility = await confirmPaidEligibility(session.uid);

      if (eligibility !== "paid") {
        return;
      }

      if (
        activeUidRef.current !== session.uid ||
        !appActiveRef.current
      ) {
        return;
      }

      const idempotencyKey = generateListingPaymentIdempotencyKey();
      const intent = await saveListingPaymentIntent({
        uid: session.uid,
        idempotencyKey,
      });
      const nextSession: ListingPaymentRuntimeSession = {
        uid: session.uid,
        intent,
        phoneNumber: cleanText(mobileNumber),
      };

      setTerminalRetryEditing(false);
      setPaymentUiState("initiating");
      updatePaymentSession(nextSession);
      await initiateSavedPayment(nextSession);
    } catch {
      if (activeUidRef.current === session.uid) {
        setAuthoritativeRecoveryState("error");
        setSessionLoadError(
          "We could not authoritatively confirm the terminal payment state. Retry before starting another payment."
        );
      }
    } finally {
      newPaymentLockRef.current = false;
      if (activeUidRef.current === session.uid) {
        setCreating(false);
      }
    }
  }, [
    confirmPaidEligibility,
    getStatusRequest,
    initiateSavedPayment,
    mobileNumber,
    paymentConfigurationState,
    processOneShotPaymentStatus,
    requireCurrentLegalAcceptance,
    terminalRetryEditing,
    updatePaymentSession,
    validateForm,
  ]);

  const refreshPaymentStatus = useCallback(async () => {
    const session = paymentSessionRef.current;

    if (
      !session ||
      activeUidRef.current !== session.uid ||
      newPaymentLockRef.current
    ) {
      return;
    }

    newPaymentLockRef.current = true;
    setSessionLoadError(null);

    try {
      await withPaymentCheckLoading(
        (busy) => {
          if (mountedRef.current && activeUidRef.current === session.uid) {
            setManualPaymentCheckBusy(busy);
          }
        },
        async () => {
          if (session.lastStatus) {
            await refreshPaymentSession(session);
            return;
          }

          if (!session.intent) return;
          if (
            !canInitiateWithPaymentConfiguration(
              paymentConfigurationState
            )
          ) {
            return;
          }

          const formIsValid = validateForm();
          const legalIsAccepted = requireCurrentLegalAcceptance();
          if (!formIsValid || !legalIsAccepted) return;

          const recovery = await getStatusRequest(session.uid);
          if (recovery.status !== "none") {
            setAuthoritativeRecoveryState("present");
            await processOneShotPaymentStatus(
              "refresh_payment_status",
              session,
              recovery
            );
            return;
          }

          setAuthoritativeRecoveryState("none");
          const nextSession: ListingPaymentRuntimeSession = {
            ...session,
            phoneNumber: cleanText(mobileNumber),
          };
          updatePaymentSession(nextSession);
          await initiateSavedPayment(nextSession);
        }
      );
    } catch {
      if (mountedRef.current && activeUidRef.current === session.uid) {
        setAuthoritativeRecoveryState("error");
        setSessionLoadError(
          "We could not check the server payment state. Retry before resuming the saved request."
        );
      }
    } finally {
      newPaymentLockRef.current = false;
      if (mountedRef.current && activeUidRef.current === session.uid) {
        setManualPaymentCheckBusy(false);
      }
    }
  }, [
    getStatusRequest,
    initiateSavedPayment,
    mobileNumber,
    paymentConfigurationState,
    processOneShotPaymentStatus,
    refreshPaymentSession,
    requireCurrentLegalAcceptance,
    updatePaymentSession,
    validateForm,
  ]);

  const retryPaidDraft = useCallback(async () => {
    const session = paymentSessionRef.current;

    if (
      session &&
      activeUidRef.current === session.uid &&
      isValidListingEntitlementId(session.entitlementId)
    ) {
      try {
        await completePaidListing({
          uid: session.uid,
          entitlementId: session.entitlementId,
        });
      } catch {
        if (activeUidRef.current === session.uid) {
          setPaymentUiState("draft_error");
        }
      }
    }
  }, [completePaidListing]);

  const retryOwnerLoad = useCallback(() => {
    if (!user || ownerLoadRetryRef.current) return;

    const request = loadAuthenticatedState(user);
    ownerLoadRetryRef.current = request;
    setOwnerLoadRetryBusy(true);
    const finishRetry = () => {
      if (ownerLoadRetryRef.current === request) {
        ownerLoadRetryRef.current = null;
        if (mountedRef.current && activeUidRef.current === user.uid) {
          setOwnerLoadRetryBusy(false);
        }
      }
    };
    void request.then(finishRetry, finishRetry);
  }, [loadAuthenticatedState, user]);

  const startBusiness = useCallback(async () => {
    if (!user || creating || legalBusy) return;

    const formIsValid = validateForm();
    const legalIsAccepted = requireCurrentLegalAcceptance();
    if (!formIsValid || !legalIsAccepted) return;

    if (startFlow !== "entitlement" && startFlow !== "pioneer") return;

    try {
      setCreating(true);

      if (startFlow === "pioneer" && !isUsablePioneerSlot(pioneerSlot)) {
        Alert.alert(
          "Listing entitlement unavailable",
          "This legacy listing entitlement can no longer be used. Retry to refresh your account or contact support."
        );
        return;
      }

      const newBusinessId = await createDraftBusiness(user.uid, {
        ownerName: cleanText(ownerName),
        businessName: cleanText(businessName),
        mobileNumber: cleanText(mobileNumber),
      });

      router.push({
        pathname: "/(owner)/onboarding/basics",
        params: { businessId: newBusinessId },
      } as any);
    } catch (error) {
      Alert.alert(
        "Could not start onboarding",
        error instanceof Error
          ? error.message
          : "We could not create your business profile. Please try again."
      );
      void loadAuthenticatedState(user);
    } finally {
      setCreating(false);
    }
  }, [
    businessName,
    creating,
    legalBusy,
    loadAuthenticatedState,
    mobileNumber,
    ownerName,
    pioneerSlot,
    requireCurrentLegalAcceptance,
    startFlow,
    user,
    validateForm,
  ]);

  if (ownerError) {
    return (
      <SafeAreaView style={styles.safeArea} edges={["top", "left", "right"]}>
        <View style={styles.loadingWrap}>
          <Text style={styles.loadingText}>
            We could not prepare your owner account. Please sign in again or
            contact support.
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea} edges={["top", "left", "right"]}>
        <View style={styles.loadingWrap}>
          <ActivityIndicator />

          <Text style={styles.loadingText}>Loading your owner account...</Text>
        </View>
      </SafeAreaView>
    );
  }

  const sectionTitle =
    startFlow === "paid"
      ? "Basic Yearly Subscription"
      : startFlow === "entitlement"
      ? "Business listing"
      : "Business listing";

  const paymentConfiguration =
    paymentConfigurationState.status === "ready"
      ? paymentConfigurationState.configuration
      : null;
  const paymentConfigurationReady =
    canInitiateWithPaymentConfiguration(paymentConfigurationState);
  const effectivePaymentLabel = paymentConfiguration
    ? formatKesAmount(paymentConfiguration.effectiveAmount)
    : null;
  const canonicalPaymentLabel = paymentConfiguration
    ? formatKesAmount(paymentConfiguration.canonicalAmount)
    : null;

  const sectionSubtitle =
    startFlow === "paid"
      ? paymentConfigurationState.status === "loading"
        ? "Loading payment details..."
        : paymentConfigurationState.status === "error"
          ? "Payment details are unavailable."
          : paymentConfiguration?.isSandboxTestPayment
            ? `Sandbox test payment: ${effectivePaymentLabel}`
            : `${effectivePaymentLabel} per year per business.`
      : startFlow === "entitlement"
      ? "Use your existing listing entitlement to create a business profile."
      : "Use your existing listing entitlement to create a business profile.";

  const statusTitle = businessToContinue
    ? "Business draft found"
    : hasPendingBusiness
    ? "Business submitted for review"
    : hasFeedbackBusiness
    ? "Business needs your attention"
    : hasApprovedBusiness
    ? "Your business is already approved"
    : startFlow === "recovery"
    ? "Account review required"
    : startFlow === "paid"
    ? paymentUiState === "verification_required" ||
      paymentUiState === "review_required"
      ? "Payment needs review"
      : paymentSession
      ? "Payment session recovered"
      : ""
    : startFlow === "entitlement"
    ? "Active listing plan ready"
    : hasUsablePioneerSlot
    ? "Existing listing entitlement ready"
    : "Existing listing record found";

  const recoveryMessage = ownerLoadError
    ? ownerLoadError
    : sessionLoadError
    ? sessionLoadError
    : hasAnyBusinessDocument
    ? "An existing business record needs recovery or support review before a paid listing can be started. This includes deleted or unrecognized business records."
    : paymentSession && hasUsablePioneerSlot
    ? "A saved payment session and an existing legacy entitlement were both found. Contact support before continuing so that a duplicate listing or payment is not created."
    : "This account has an existing legacy listing record that cannot be used for a new paid listing. Open the dashboard or contact support for recovery help.";

  const paymentMessage = paymentMessageForState(paymentUiState);
  const paymentIsTerminal =
    paymentSession?.lastStatus !== undefined &&
    isFailedListingPaymentStatus(paymentSession.lastStatus);
  const paymentIsBusy =
    creating ||
    manualPaymentCheckBusy ||
    paymentUiState === "initiating" ||
    paymentUiState === "polling" ||
    paymentUiState === "creating_draft";
  const showForm =
    startFlow === "entitlement" ||
    startFlow === "pioneer" ||
    startFlow === "paid";
  const paymentFormLocked =
    startFlow === "paid" &&
    !!paymentSession?.lastStatus &&
    !isFailedListingPaymentStatus(paymentSession.lastStatus);
  const requiresPaymentSupport =
    paymentUiState === "verification_required" ||
    paymentUiState === "review_required";
  const isPaidFlow = startFlow === "paid";
  const hasEstablishedOwnerPath =
    !!businessToContinue ||
    hasLockedExistingBusiness ||
    hasEntitlementSlot ||
    pioneerSlot?.status === "used";
  const blockingLoadError =
    ownerLoadError || (!hasEstablishedOwnerPath ? sessionLoadError : null);
  const showListingForm = showForm;
  const formLocked = paymentFormLocked || creating;
  const retryPhoneNumber = cleanText(mobileNumber);
  const requiredPaymentFieldsAreValid =
    !!cleanText(businessName) && isValidKenyanMobile(retryPhoneNumber);
  const paymentFailureAnnouncement =
    startFlow === "recovery" || requiresPaymentSupport || paymentIsTerminal;
  const statusAnnouncement = [
    statusTitle,
    startFlow === "recovery" ? recoveryMessage : paymentMessage,
  ]
    .filter(Boolean)
    .join(". ");
  const newPaymentIsReady =
    requiredPaymentFieldsAreValid &&
    paymentConfigurationReady &&
    hasCurrentLegalAcceptance &&
    !legalBusy &&
    !creating &&
    canCreateNewListingPaymentKey({
      recoveryState: authoritativeRecoveryState,
      hasRuntimeSession: !!paymentSession,
    });

  const positiveStatus =
    !!businessToContinue ||
    hasUsablePioneerSlot ||
    hasPendingBusiness ||
    hasApprovedBusiness ||
    hasEntitlementSlot;

  const statusBoxStyle = [
    styles.statusBox,
    startFlow === "recovery" || requiresPaymentSupport || paymentIsTerminal
      ? styles.statusBoxDanger
      : positiveStatus
      ? styles.statusBoxPositive
      : styles.statusBoxInfo,
  ];

  const statusTextStyle = [
    styles.statusTitle,
    startFlow === "recovery" || requiresPaymentSupport || paymentIsTerminal
      ? styles.statusTextDanger
      : positiveStatus
      ? styles.statusTextPositive
      : styles.statusTextInfo,
  ];
  const showStatusBox = !!statusTitle;

  return (
    <SafeAreaView style={styles.safeArea} edges={["top", "left", "right"]}>
      <KeyboardAvoidingView
        style={styles.keyboardView}
        behavior={
          Platform.OS === "ios"
            ? "padding"
            : Platform.OS === "android"
            ? "height"
            : undefined
        }
        keyboardVerticalOffset={Platform.OS === "ios" ? 8 : 0}
      >
        <ScrollView
          ref={scrollRef}
          style={styles.scroll}
          contentContainerStyle={[
            styles.scrollContent,
            {
              paddingBottom: Math.max(128, insets.bottom + 120),
            },
          ]}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode={Platform.OS === "ios" ? "interactive" : "on-drag"}
          contentInsetAdjustmentBehavior="automatic"
          tabIndex={Platform.OS === "web" ? 0 : undefined}
        >
          <View style={styles.container}>
            <View style={styles.card}>
              <View style={styles.heroHeader}>
                <SokoGridLogo size={52} />

                <View style={styles.heroCopy}>
                  <Text style={styles.heroTitle}>Welcome to Soko Grid</Text>

                  <Text style={styles.heroSubtitle}>
                    Create your online storefront and business directory profile.
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.card}>
              <Text style={styles.sectionTitle}>
                {sectionTitle}
              </Text>

              {isPaidFlow ? (
                <View style={styles.subscriptionSummary}>
                  <BadgeCheck size={22} color="#854D0E" strokeWidth={2.4} />
                  <View style={styles.subscriptionSummaryCopy}>
                    <Text style={styles.subscriptionSummaryText}>
                      {sectionSubtitle}
                    </Text>
                    {paymentConfiguration?.isSandboxTestPayment ? (
                      <Text style={styles.subscriptionSummaryText}>
                        Production subscription price remains{" "}
                        {canonicalPaymentLabel} per year.
                      </Text>
                    ) : null}
                  </View>
                </View>
              ) : (
                <Text style={styles.sectionSubtitle}>{sectionSubtitle}</Text>
              )}

              {isPaidFlow ? (
                <View style={styles.subscriptionDetails}>
                  <Text style={styles.subscriptionDetail}>Payment: M-Pesa</Text>
                  <Text style={styles.subscriptionDetail}>
                    Review: Within 24 hours
                  </Text>
                  {paymentConfigurationState.status === "error" && user ? (
                    <Pressable
                      accessibilityLabel="Retry loading payment details"
                      accessibilityRole="button"
                      onPress={() =>
                        void reloadPaymentConfiguration(user.uid)
                      }
                      style={styles.inlineRetryButton}
                    >
                      <Text style={styles.inlineRetryButtonText}>
                        Retry payment details
                      </Text>
                    </Pressable>
                  ) : null}
                </View>
              ) : null}

              {showStatusBox ? (
                <View
                  accessible
                  accessibilityLabel={statusAnnouncement}
                  accessibilityLiveRegion={
                    paymentFailureAnnouncement ? "assertive" : "polite"
                  }
                  accessibilityRole={
                    paymentFailureAnnouncement ? "alert" : undefined
                  }
                  style={statusBoxStyle}
                >
                  <Text accessible={false} style={statusTextStyle}>
                    {statusTitle}
                  </Text>

                  {hasEntitlementSlot ? (
                    <Text style={styles.entitlementText}>
                      Active listing plan found. You can create{" "}
                      {remainingEntitlementSlots} business profile
                      {remainingEntitlementSlots === 1 ? "" : "s"}.
                      {formatDate((entitlement as any)?.endsAt)
                        ? ` Valid until ${formatDate(
                            (entitlement as any)?.endsAt
                          )}.`
                        : ""}
                    </Text>
                  ) : null}

                  {startFlow === "recovery" ? (
                    <Text
                      accessible={false}
                      style={styles.statusGuidance}
                    >
                      {recoveryMessage}
                    </Text>
                  ) : paymentMessage ? (
                    <Text
                      accessible={false}
                      style={styles.statusGuidance}
                    >
                      {paymentMessage}
                    </Text>
                  ) : null}

                  {isPaidFlow && paymentSession?.maskedPhone ? (
                    <Text style={styles.statusGuidance}>
                      M-Pesa number: {paymentSession.maskedPhone}
                    </Text>
                  ) : null}

                  {isPaidFlow && paymentIsTerminal && terminalRetryEditing ? (
                    <Text
                      accessibilityLiveRegion="polite"
                      style={styles.statusGuidance}
                    >
                      Confirm the corrected number. The new STK prompt will be
                      sent to {retryPhoneNumber || "the number entered below"}.
                    </Text>
                  ) : null}
                </View>
              ) : null}

              {localPrivacyWarning ? (
                <Text
                  accessibilityLiveRegion="polite"
                  style={styles.statusGuidance}
                >
                  {localPrivacyWarning}
                </Text>
              ) : null}

              {showListingForm ? (
                <View style={styles.form}>
                  <View>
                    <Text style={styles.label}>Name</Text>

                    <TextInput
                      accessibilityLabel="Name"
                      accessibilityHint="Enter the name used for business onboarding."
                      accessibilityState={{ disabled: formLocked }}
                      value={ownerName}
                      editable={!formLocked}
                      onChangeText={setOwnerName}
                      placeholder="Your full name"
                      placeholderTextColor="#9CA3AF"
                      returnKeyType="next"
                      style={[
                        styles.input,
                        formLocked ? styles.disabledInput : null,
                      ]}
                    />
                  </View>

                  <View>
                    <Text style={styles.label}>Email address</Text>

                    <TextInput
                      accessibilityLabel="Email address"
                      accessibilityState={{ disabled: true }}
                      value={gmailEmail}
                      editable={false}
                      placeholder="Signed-in Google email"
                      placeholderTextColor="#9CA3AF"
                      style={[styles.input, styles.disabledInput]}
                    />
                  </View>

                  <View>
                    <Text style={styles.label}>Business name *</Text>

                    <TextInput
                      accessibilityLabel="Business name, required"
                      accessibilityHint="Enter the business name to continue."
                      accessibilityState={{ disabled: formLocked }}
                      ref={businessNameInputRef}
                      value={businessName}
                      editable={!formLocked}
                      onChangeText={(value) => {
                        setBusinessName(value);
                        if (cleanText(value)) {
                          setFieldErrors((previous) => {
                            if (!previous.businessName) return previous;

                            const next = { ...previous };
                            delete next.businessName;
                            return next;
                          });
                        }
                      }}
                      onBlur={() => {
                        if (!cleanText(businessName)) {
                          setFieldErrors((previous) => ({
                            ...previous,
                            businessName: "Enter your business name.",
                          }));
                        }
                      }}
                      placeholder="Example: Smart Salon"
                      placeholderTextColor="#9CA3AF"
                      returnKeyType="next"
                      style={[
                        styles.input,
                        formLocked ? styles.disabledInput : null,
                        fieldErrors.businessName ? styles.inputError : null,
                      ]}
                    />

                    {fieldErrors.businessName ? (
                      <Text accessibilityRole="alert" style={styles.errorText}>
                        {fieldErrors.businessName}
                      </Text>
                    ) : null}
                  </View>

                  <View>
                    <Text style={styles.label}>M-Pesa mobile number *</Text>

                    <TextInput
                      accessibilityLabel="M-Pesa mobile number, required"
                      accessibilityHint={
                        paymentIsTerminal
                          ? "Edit the number before explicitly confirming a new payment attempt."
                          : "Enter the Kenyan number that should receive the M-Pesa STK prompt."
                      }
                      accessibilityState={{ disabled: formLocked }}
                      ref={mobileNumberInputRef}
                      value={mobileNumber}
                      editable={!formLocked}
                      onChangeText={(value) => {
                        setMobileNumber(value);
                        if (isValidKenyanMobile(value)) {
                          setFieldErrors((previous) => {
                            if (!previous.mobileNumber) return previous;

                            const next = { ...previous };
                            delete next.mobileNumber;
                            return next;
                          });
                        }
                      }}
                      onBlur={() => {
                        if (!isValidKenyanMobile(cleanText(mobileNumber))) {
                          setFieldErrors((previous) => ({
                            ...previous,
                            mobileNumber:
                              "Enter a valid Kenyan M-Pesa mobile number.",
                          }));
                        }
                      }}
                      onFocus={scrollToFormBottom}
                      placeholder="+2547..."
                      placeholderTextColor="#9CA3AF"
                      keyboardType="phone-pad"
                      textContentType="telephoneNumber"
                      returnKeyType="done"
                      style={[
                        styles.input,
                        formLocked ? styles.disabledInput : null,
                        fieldErrors.mobileNumber ? styles.inputError : null,
                      ]}
                    />

                    {fieldErrors.mobileNumber ? (
                      <Text accessibilityRole="alert" style={styles.errorText}>
                        {fieldErrors.mobileNumber}
                      </Text>
                    ) : null}
                  </View>

                </View>
              ) : null}

              <View style={styles.actions}>
                {formError ? (
                  <Text accessibilityRole="alert" style={styles.formErrorText}>
                    {formError}
                  </Text>
                ) : null}

                {requiresLegalAcceptance ? (
                  <LegalAcceptance
                    checked={hasCurrentLegalAcceptance}
                    error={legalError}
                    loading={legalChecking}
                    onChange={acceptLegalTerms}
                    returnTo="/(owner)/start"
                    saving={legalSaving}
                  />
                ) : null}

                {blockingLoadError ? (
                  <Pressable
                    accessibilityHint="Checks the server payment state and owner eligibility again."
                    accessibilityLabel="Retry account and payment check"
                    accessibilityRole="button"
                    accessibilityState={{
                      busy: ownerLoadRetryBusy,
                      disabled: ownerLoadRetryBusy,
                    }}
                    disabled={ownerLoadRetryBusy}
                    onPress={retryOwnerLoad}
                    style={[
                      styles.secondaryButton,
                      ownerLoadRetryBusy ? styles.primaryButtonDisabled : null,
                    ]}
                  >
                    <Text style={styles.secondaryButtonText}>
                      {ownerLoadRetryBusy
                        ? "Checking account..."
                        : "Retry account check"}
                    </Text>
                  </Pressable>
                ) : businessToContinue ? (
                  <Pressable
                    disabled={listingCtaDisabled}
                    onPress={continueExistingBusiness}
                    style={[
                      styles.primaryButton,
                      listingCtaDisabled ? styles.primaryButtonDisabled : null,
                    ]}
                  >
                    <Text style={styles.primaryButtonText}>
                      Resume business onboarding
                    </Text>
                  </Pressable>
                ) : startFlow === "dashboard" ? (
                  <Pressable
                    onPress={() => router.push("/(owner)/dashboard" as any)}
                    style={styles.primaryButton}
                  >
                    <Text style={styles.primaryButtonText}>
                      {hasPendingBusiness
                        ? "View submitted business"
                        : hasFeedbackBusiness
                        ? "Review required changes"
                        : hasApprovedBusiness
                        ? "View your business"
                        : "Go to dashboard"}
                    </Text>
                  </Pressable>
                ) : startFlow === "recovery" ? (
                  <View style={styles.recoveryActions}>
                    {hasAnyBusinessDocument ? (
                      <Pressable
                        onPress={() =>
                          router.push("/(owner)/dashboard" as any)
                        }
                        style={styles.secondaryButton}
                      >
                        <Text style={styles.secondaryButtonText}>
                          Go to dashboard
                        </Text>
                      </Pressable>
                    ) : null}

                    <Pressable
                      onPress={() => router.push("/contact" as any)}
                      style={styles.secondaryButton}
                    >
                      <Text style={styles.secondaryButtonText}>
                        Contact support
                      </Text>
                    </Pressable>
                  </View>
                ) : isPaidFlow ? (
                  paymentUiState === "creating_draft" ? (
                    <Pressable
                      accessibilityLabel="Preparing paid business profile"
                      accessibilityRole="button"
                      accessibilityState={{ disabled: true, busy: true }}
                      disabled
                      style={[styles.primaryButton, styles.primaryButtonDisabled]}
                    >
                      <Text style={styles.primaryButtonText}>
                        Preparing business profile...
                      </Text>
                    </Pressable>
                  ) : paymentUiState === "draft_error" ? (
                    <Pressable
                      accessibilityHint="Retries deterministic setup with the same verified entitlement."
                      accessibilityLabel="Retry paid business setup"
                      accessibilityRole="button"
                      onPress={retryPaidDraft}
                      style={styles.primaryButton}
                    >
                      <Text style={styles.primaryButtonText}>
                        Retry business setup
                      </Text>
                    </Pressable>
                  ) : paymentUiState === "authentication_required" ? (
                    <Pressable
                      accessibilityHint="Refreshes authentication without sending a payment request."
                      accessibilityLabel="Refresh sign-in before checking saved payment"
                      accessibilityRole="button"
                      accessibilityState={{ disabled: creating, busy: creating }}
                      disabled={creating}
                      onPress={() => void refreshPaymentAuthentication()}
                      style={[
                        styles.secondaryButton,
                        creating ? styles.primaryButtonDisabled : null,
                      ]}
                    >
                      <Text style={styles.secondaryButtonText}>
                        {creating ? "Refreshing sign-in..." : "Refresh sign-in"}
                      </Text>
                    </Pressable>
                  ) : paymentUiState === "permission_denied" ? (
                    <Pressable
                      accessibilityHint="Opens support without starting another payment."
                      accessibilityLabel="Contact support about account payment permission"
                      accessibilityRole="button"
                      onPress={() => router.push("/contact" as any)}
                      style={styles.secondaryButton}
                    >
                      <Text style={styles.secondaryButtonText}>
                        Contact support
                      </Text>
                    </Pressable>
                  ) : paymentUiState === "verification_required" ||
                    paymentUiState === "review_required" ? (
                    <Pressable
                      accessibilityHint="Opens support without starting another payment."
                      accessibilityLabel="Contact support about payment review"
                      accessibilityRole="button"
                      onPress={() => router.push("/contact" as any)}
                      style={styles.secondaryButton}
                    >
                      <Text style={styles.secondaryButtonText}>
                        Contact support
                      </Text>
                    </Pressable>
                  ) : paymentUiState === "failed" ||
                    paymentUiState === "cancelled" ||
                    paymentUiState === "timed_out" ? (
                    terminalRetryEditing ? (
                      <Pressable
                        accessibilityHint="Creates a new request key only after the server confirms the previous attempt is terminal."
                        accessibilityLabel={`Confirm new M-Pesa payment to ${
                          retryPhoneNumber || "the entered number"
                        }`}
                        accessibilityRole="button"
                        accessibilityState={{
                          disabled:
                            listingCtaDisabled ||
                            !requiredPaymentFieldsAreValid ||
                            !paymentConfigurationReady,
                          busy: creating,
                        }}
                        onPress={() => void retryTerminalPayment()}
                        disabled={
                          listingCtaDisabled ||
                          !requiredPaymentFieldsAreValid ||
                          !paymentConfigurationReady
                        }
                        style={[
                          styles.primaryButton,
                          listingCtaDisabled ||
                          !requiredPaymentFieldsAreValid ||
                          !paymentConfigurationReady
                            ? styles.primaryButtonDisabled
                            : null,
                        ]}
                      >
                        <Text style={styles.primaryButtonText}>
                          Confirm new payment to {retryPhoneNumber}
                        </Text>
                      </Pressable>
                    ) : (
                      <Pressable
                        accessibilityHint="Refreshes the terminal status before allowing phone correction or a new payment."
                        accessibilityLabel="Review phone number for a new M-Pesa payment"
                        accessibilityRole="button"
                        accessibilityState={{
                          disabled: listingCtaDisabled,
                          busy: creating,
                        }}
                        onPress={() => void prepareTerminalRetry()}
                        disabled={listingCtaDisabled}
                        style={[
                          styles.primaryButton,
                          listingCtaDisabled
                            ? styles.primaryButtonDisabled
                            : null,
                        ]}
                      >
                        <Text style={styles.primaryButtonText}>
                          Review phone number for new payment
                        </Text>
                      </Pressable>
                    )
                  ) : paymentIsBusy ? (
                    <Pressable
                      accessibilityLabel={
                        paymentUiState === "initiating"
                          ? "Sending M-Pesa request"
                          : "Checking payment status"
                      }
                      accessibilityRole="button"
                      accessibilityState={{ disabled: true, busy: true }}
                      disabled
                      style={[
                        styles.primaryButton,
                        styles.primaryButtonDisabled,
                      ]}
                    >
                      <Text style={styles.primaryButtonText}>
                        {paymentUiState === "initiating"
                          ? "Sending M-Pesa request..."
                          : "Checking payment status..."}
                      </Text>
                    </Pressable>
                  ) : paymentSession ? (
                    <Pressable
                      accessibilityHint={
                        paymentSession.lastStatus
                          ? "Refreshes the authoritative payment state from the server."
                          : "Checks the server first, then retries with the same saved request key."
                      }
                      accessibilityLabel={
                        paymentSession.lastStatus
                          ? "Refresh payment status"
                          : `Retry saved M-Pesa request to ${
                              retryPhoneNumber || "the entered number"
                            }`
                      }
                      accessibilityRole="button"
                      accessibilityState={{
                        disabled:
                          !paymentSession.lastStatus &&
                          (listingCtaDisabled ||
                            !requiredPaymentFieldsAreValid ||
                            !paymentConfigurationReady),
                        busy: creating,
                      }}
                      onPress={refreshPaymentStatus}
                      disabled={
                        !paymentSession.lastStatus &&
                        (listingCtaDisabled ||
                          !requiredPaymentFieldsAreValid ||
                          !paymentConfigurationReady)
                      }
                      style={[
                        styles.secondaryButton,
                        !paymentSession.lastStatus &&
                        (listingCtaDisabled ||
                          !requiredPaymentFieldsAreValid ||
                          !paymentConfigurationReady)
                          ? styles.primaryButtonDisabled
                          : null,
                      ]}
                    >
                      <Text style={styles.secondaryButtonText}>
                        {paymentSession.lastStatus
                          ? "Check payment status"
                          : "Retry saved M-Pesa request"}
                      </Text>
                    </Pressable>
                  ) : (
                    <Pressable
                      accessibilityHint="Checks the server and eligibility before securely saving a short-lived request key and sending the M-Pesa prompt."
                      accessibilityLabel={
                        effectivePaymentLabel
                          ? `Pay ${effectivePaymentLabel} with M-Pesa`
                          : "Payment details unavailable"
                      }
                      accessibilityRole="button"
                      accessibilityState={{
                        disabled: !newPaymentIsReady,
                        busy: creating,
                      }}
                      onPress={() => void startPaidPayment()}
                      disabled={!newPaymentIsReady}
                      style={[
                        styles.primaryButton,
                        !newPaymentIsReady
                          ? styles.primaryButtonDisabled
                          : null,
                      ]}
                    >
                      <Text style={styles.primaryButtonText}>
                        {effectivePaymentLabel
                          ? `Pay ${effectivePaymentLabel} with M-Pesa`
                          : paymentConfigurationState.status === "loading"
                            ? "Loading payment details..."
                            : "Payment details unavailable"}
                      </Text>
                    </Pressable>
                  )
                ) : (
                  <Pressable
                    onPress={startBusiness}
                    disabled={creating || listingCtaDisabled}
                    style={[
                      styles.primaryButton,
                      creating || listingCtaDisabled
                        ? styles.primaryButtonDisabled
                        : null,
                    ]}
                  >
                    <Text style={styles.primaryButtonText}>
                      {creating
                        ? "Starting onboarding..."
                        : "Create business profile"}
                    </Text>
                  </Pressable>
                )}

                {sessionLoadError && !hasEstablishedOwnerPath ? (
                  <Pressable
                    onPress={() => router.push("/contact" as any)}
                    style={styles.secondaryButton}
                  >
                    <Text style={styles.secondaryButtonText}>
                      Contact support
                    </Text>
                  </Pressable>
                ) : null}
              </View>
            </View>

            {hasAnyBusiness ? (
              <View style={styles.card}>
                <Text style={styles.sectionTitle}>Your businesses</Text>

                <View style={styles.businessList}>
                  {visibleBusinesses.map((biz) => (
                    <View key={biz.id} style={styles.businessItem}>
                      <Text style={styles.businessName}>
                        {biz.name?.trim() || "Untitled business"}
                      </Text>

                      <Text style={styles.businessStatus}>
                        Status: {biz.status || "draft"}
                      </Text>
                    </View>
                  ))}
                </View>

                <Pressable
                  onPress={() => router.push("/(owner)/dashboard" as any)}
                  style={styles.secondaryButton}
                >
                  <Text style={styles.secondaryButtonText}>Go to dashboard</Text>
                </Pressable>
              </View>
            ) : null}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    minHeight: 0,
    backgroundColor: "#F9FAFB",
  },

  keyboardView: {
    flex: 1,
    minHeight: 0,
  },

  scroll: {
    flex: 1,
    minHeight: 0,
    backgroundColor: "#F9FAFB",
  },

  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 22,
  },

  container: {
    width: "100%",
    maxWidth: 720,
    alignSelf: "center",
    gap: 14,
  },

  loadingWrap: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F9FAFB",
    padding: 20,
  },

  loadingText: {
    marginTop: 12,
    fontWeight: "900",
    color: "#111827",
  },

  card: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 18,
    padding: 16,
  },

  heroHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },

  heroCopy: {
    flex: 1,
    minWidth: 0,
  },

  heroTitle: {
    fontSize: 25,
    lineHeight: 32,
    fontWeight: "900",
    color: "#111827",
  },

  heroSubtitle: {
    marginTop: 8,
    fontSize: 14,
    lineHeight: 22,
    color: "#6B7280",
    fontWeight: "700",
  },

  sectionTitle: {
    fontSize: 20,
    lineHeight: 26,
    fontWeight: "900",
    color: "#111827",
  },

  sectionSubtitle: {
    marginTop: 8,
    fontSize: 14,
    lineHeight: 22,
    color: "#6B7280",
    fontWeight: "700",
  },

  subscriptionSummary: {
    marginTop: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#FDE68A",
    backgroundColor: "#FFFBEB",
    padding: 16,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
  },

  subscriptionSummaryText: {
    color: "#713F12",
    fontSize: 14,
    lineHeight: 21,
    fontWeight: "800",
  },

  subscriptionSummaryCopy: {
    flex: 1,
    gap: 4,
  },

  subscriptionDetails: {
    marginTop: 10,
    gap: 4,
  },

  inlineRetryButton: {
    alignSelf: "flex-start",
    marginTop: 6,
    paddingVertical: 6,
  },

  inlineRetryButtonText: {
    color: "#1D4ED8",
    fontSize: 14,
    fontWeight: "800",
  },

  subscriptionDetail: {
    color: "#4B5563",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
  },

  statusBox: {
    marginTop: 14,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderRadius: 16,
    borderWidth: 1,
  },

  statusBoxPositive: {
    backgroundColor: "#DCFCE7",
    borderColor: "#86EFAC",
  },

  statusBoxDanger: {
    backgroundColor: "#FEE2E2",
    borderColor: "#FCA5A5",
  },

  statusBoxInfo: {
    backgroundColor: "#EFF6FF",
    borderColor: "#BFDBFE",
  },

  statusTitle: {
    fontSize: 15,
    lineHeight: 21,
    fontWeight: "900",
  },

  statusTextPositive: {
    color: "#166534",
  },

  statusTextDanger: {
    color: "#991B1B",
  },

  statusTextInfo: {
    color: "#1E40AF",
  },

  entitlementText: {
    marginTop: 6,
    color: "#166534",
    fontWeight: "700",
    lineHeight: 20,
  },

  statusGuidance: {
    marginTop: 6,
    color: "#374151",
    fontWeight: "700",
    lineHeight: 20,
  },

  form: {
    marginTop: 16,
    gap: 12,
  },

  label: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "900",
    color: "#111827",
  },

  input: {
    marginTop: 6,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: Platform.OS === "ios" ? 13 : 11,
    minHeight: 52,
    color: "#111827",
    backgroundColor: "#FFFFFF",
    fontWeight: "800",
    fontSize: 15,
  },

  disabledInput: {
    color: "#6B7280",
    backgroundColor: "#F3F4F6",
  },

  inputError: {
    borderColor: "#DC2626",
    backgroundColor: "#FEF2F2",
  },

  errorText: {
    marginTop: 6,
    color: "#B91C1C",
    fontWeight: "800",
    fontSize: 12,
    lineHeight: 18,
  },

  actions: {
    marginTop: 16,
    gap: 10,
  },

  recoveryActions: {
    gap: 10,
  },

  formErrorText: {
    color: "#B91C1C",
    backgroundColor: "#FEF2F2",
    borderWidth: 1,
    borderColor: "#FCA5A5",
    borderRadius: 12,
    padding: 10,
    fontWeight: "800",
    fontSize: 12,
    lineHeight: 18,
  },

  primaryButton: {
    backgroundColor: "#111827",
    borderRadius: 14,
    paddingVertical: 15,
    paddingHorizontal: 14,
    alignItems: "center",
  },

  primaryButtonDisabled: {
    backgroundColor: "#9CA3AF",
  },

  primaryButtonText: {
    color: "#FFFFFF",
    fontWeight: "900",
    fontSize: 14,
  },

  secondaryButton: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 14,
    paddingVertical: 15,
    paddingHorizontal: 14,
    alignItems: "center",
  },

  secondaryButtonText: {
    color: "#111827",
    fontWeight: "900",
    fontSize: 14,
  },

  businessList: {
    marginTop: 10,
    gap: 8,
  },

  businessItem: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 14,
    padding: 12,
    backgroundColor: "#F9FAFB",
  },

  businessName: {
    fontWeight: "900",
    color: "#111827",
  },

  businessStatus: {
    marginTop: 4,
    color: "#6B7280",
    fontWeight: "700",
  },
});
